
import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  ChevronRight,
  Loader,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBlog } from './BlogBackend';
import { trackPageView } from '../../utlis/useAnalytics';
import { PageName } from '../../configs/config';
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  loading = false,
}) => {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    // Calculate start and end of middle section
    const start = Math.max(2, currentPage - delta);
    const end = Math.min(totalPages - 1, currentPage + delta);

    // Add ellipsis after first page if needed
    if (start > 2) {
      pages.push('...');
    }

    // Add middle pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (end < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page (if not already included)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-center space-x-1 mt-8">
      {/* Previous button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || loading}
        className="flex items-center px-3 py-2 text-sm hover:cursor-pointer font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight size={16} className="mr-1 rotate-180" />
        Previous
      </button>

      {/* Page numbers */}
      <div className="flex space-x-1">
        {visiblePages.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-2 text-sm font-medium text-gray-700"
              >
                …
              </span>
            );
          }

          const pageNum = page as number;
          const isActive = pageNum === currentPage;

          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                isActive
                  ? 'bg-[#27a887] text-white border border-[#27a887]'
                  : 'text-gray-700 bg-white border border-gray-300 hover:cursor-pointer hover:text-gray-900 hover:bg-gray-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      {/* Next button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || loading}
        className="flex items-center px-3 hover:cursor-pointer py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
        <ChevronRight size={16} className="ml-1" />
      </button>
    </div>
  );
};

// -----------------------------------------------------------------------------
// BlogSection: Lists all posts (grid of cards, tag‐filters, pagination, etc.)
// -----------------------------------------------------------------------------
const BlogSection: React.FC = () => {
  // Track page view on mount
  useEffect(() => {
    trackPageView(PageName.Blog);
  }, []);

  // We still call useParams/useNavigate to satisfy useBlog,
  // but `slug` will be undefined in this listing view.
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const blog = useBlog({ slug, navigate });

  return (
    <>
      {/* -----------------------------------------------------------------------
          SEO Meta Tags for the listing page
      ----------------------------------------------------------------------- */}
      <Helmet>
        <title>Discover Insights on Global Trade, Finance & Tax | Eximpe Blog</title>
        <meta
          name="description"
          content="Stay updated with the latest trends and expert insights on import/export, business finance, tax planning, and compliance in India. Read Eximpe's blog!"
        />
        <link rel="canonical" href={`${window.location.origin}/blog`} />

        {/* Open Graph */}
        <meta property="og:title" content="Discover Insights on Global Trade, Finance & Tax | Eximpe Blog" />
        <meta
          property="og:description"
          content="Stay updated with the latest trends and expert insights on import/export, business finance, tax planning, and compliance in India. Read Eximpe's blog!"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/blog`} />
        <meta property="og:site_name" content="Eximpe Blog" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Discover Insights on Global Trade, Finance & Tax | Eximpe Blog" />
        <meta
          name="twitter:description"
          content="Stay updated with the latest trends and expert insights on import/export, business finance, tax planning, and compliance in India. Read Eximpe's blog!"
        />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Eximpe Blog",
            description:
              "Stay updated with the latest trends and expert insights on import/export, business finance, tax planning, and compliance in India. Read Eximpe's blog!",
            url: `${window.location.origin}/blog`,
            publisher: {
              "@type": "Organization",
              name: "Eximpe",
              logo: {
                "@type": "ImageObject",
                url: `${window.location.origin}/logo.png`,
              },
            },
          })}
        </script>
      </Helmet>

      <section className="py-16 bg-gray-50 mt-10 lg:px-[13%]">
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Header */}
              <div className="max-w-3xl mx-auto text-center mb-12">
                <h1 className="text-4xl font-bold mb-4">Our Blog</h1>
                <p className="text-gray-600">
                  Insights and updates on global payments and international banking.
                </p>
              </div>

              {/* Tag Filters */}
              <div className="flex flex-wrap gap-2 mb-10 justify-center sm:justify-end">
                <button
                  onClick={() => blog.handleTagSelect(null, 'all')}
                  disabled={blog.loadingTagId !== null}
                  className={`${
                    blog.selectedTagSlug === null
                      ? 'bg-[#27a887] text-white'
                      : 'bg-gray-100 text-gray-700'
                  } px-4 py-2 rounded-full text-sm flex hover:cursor-pointer items-center`}
                >
                  {blog.loadingTagId === 'all' && (
                    <Loader size={14} className="animate-spin mr-2" />
                  )}
                  All
                </button>
                {blog.tags.slice(0, 5).map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => blog.handleTagSelect(tag.slug, tag.id)}
                    disabled={blog.loadingTagId !== null}
                    className={`${
                      blog.selectedTagSlug === tag.slug
                        ? 'bg-[#27a887] text-white'
                        : 'bg-gray-100 text-gray-700'
                    } px-4 py-2 rounded-full text-sm flex hover:cursor-pointer items-center`}
                  >
                    {blog.loadingTagId === tag.id && (
                      <Loader size={14} className="animate-spin mr-2" />
                    )}
                    {tag.name}
                  </button>
                ))}
              </div>

              {/* Showing X–Y of Z posts */}
              {!blog.loadingPosts && blog.posts.length > 0 && blog.totalPosts > 0 && (
                <div className="text-center mb-6 text-gray-600 text-sm">
                  Showing {((blog.currentPage - 1) * 6) + 1} –{' '}
                  {Math.min(blog.currentPage * 6, blog.totalPosts)} of {blog.totalPosts} posts
                </div>
              )}

              {/* Loading Skeleton */}
              {(blog.loadingPosts || blog.posts.length === 0) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="h-48 bg-gray-200" />
                      <div className="p-4 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4" />
                        <div className="h-6 bg-gray-200 rounded w-3/4" />
                        <div className="h-4 bg-gray-200 rounded w-full" />
                        <div className="h-4 bg-gray-200 rounded w-full" />
                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {blog.posts.map((post) => {
                    const tagSlug =
                      post.primary_tag?.slug || post.tags[0]?.slug || 'uncategorized';
                    const imgUrl = blog.getFeaturedImageUrl(post);

                    return (
                      <Link
                        key={post.id}
                        to={`/blog/${tagSlug}/${post.slug}`}
                        className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-300 cursor-pointer no-underline text-current"
                      >
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={post.title}
                            className="w-full h-65 object-cover rounded-t-2xl"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gray-200" />
                        )}
                        <div className="p-4">
                          <div className="flex flex-wrap items-center text-xs text-gray-500 mb-2 gap-2">
                            <div className="flex items-center">
                              <Calendar size={12} className="mr-1" />
                              <span>{blog.formatDate(post.published_at)}</span>
                            </div>
                            {post.primary_tag && (
                              <span className="bg-[#e6fbf8] text-[#27a887] px-2 py-0.5 rounded-full text-xs">
                                {post.primary_tag.name}
                              </span>
                            )}
                          </div>
                          <h2 className="text-lg font-semibold mb-2 line-clamp-2">
                            {post.title}
                          </h2>
                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {blog.stripHtml(post.excerpt)}
                          </p>
                          {post.authors?.length > 0 && (
                            <div className="flex items-center mb-3 text-xs text-gray-500">
                              <Users size={12} className="mr-1" />
                              <span>{post.authors.map((a) => a.name).join(', ')}</span>
                            </div>
                          )}
                          <div className="inline-flex items-center text-[#27a887] font-medium group">
                            Read More
                            <ChevronRight
                              size={16}
                              className="ml-1 group-hover:translate-x-1 transition-transform duration-300"
                            />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {!blog.loadingPosts && blog.posts.length > 0 && (
                <Pagination
                  currentPage={blog.currentPage}
                  totalPages={blog.totalPages}
                  onPageChange={blog.handlePageChange}
                  loading={blog.loadingPosts}
                />
              )}

              {/* Error */}
              {blog.error && (
                <p className="text-red-500 mt-4 text-center">{blog.error}</p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </>
  );
};

export default BlogSection;
